/**
 * PHI (Protected Health Information) Type Definitions
 * HIPAA-compliant encrypted data types and utilities
 *
 * PHI includes any individually identifiable health information:
 * - Names, addresses, phone numbers, email
 * - Medical Record Numbers (MRN)
 * - Dates of birth
 * - Insurance information
 * - Clinical notes and diagnoses
 */

/**
 * Encrypted field wrapper type
 * Stores encrypted data with metadata for decryption
 */
export interface EncryptedField {
  /** Encrypted data as base64 string */
  encrypted: string;
  /** Initialization vector (IV) as base64 string */
  iv: string;
  /** Algorithm used for encryption */
  algorithm: 'AES-GCM';
  /** Timestamp when encrypted */
  encryptedAt: number;
}

/**
 * PHI Data Classification
 * Used to determine encryption requirements and audit logging
 */
export enum PHIClassification {
  /** Directly identifiable information (name, MRN, SSN) */
  DIRECT_IDENTIFIER = 'direct_identifier',
  /** Quasi-identifiers (date of birth, zip code) */
  QUASI_IDENTIFIER = 'quasi_identifier',
  /** Sensitive health information (diagnoses, medications) */
  SENSITIVE_HEALTH = 'sensitive_health',
  /** Contact information (email, phone) */
  CONTACT_INFO = 'contact_info',
  /** Insurance/billing information */
  FINANCIAL_INFO = 'financial_info',
  /** Health information (diagnoses, medications) */
  HEALTH_INFO = 'health_info',
  /** Non-PHI information (address, phone number) */
  NON_PHI = 'non_phi',
}

/**
 * De-identification level for displaying PHI
 * Controls how much patient information is visible in the UI
 *
 * - 'none': Full patient information visible (requires proper authorization)
 * - 'limited': Partial masking (e.g., "John D." instead of "John Doe")
 * - 'full': Complete de-identification (e.g., "Patient 123456")
 */
export type DeIdentificationLevel = 'none' | 'limited' | 'full';

/**
 * Encryption key metadata
 * Used for key rotation and audit trails
 */
export interface EncryptionKeyInfo {
  /** Key identifier (not the key itself) */
  keyId: string;
  /** Key creation timestamp */
  createdAt: number;
  /** Key rotation status */
  rotationStatus: 'active' | 'rotating' | 'deprecated';
  /** Algorithm used */
  algorithm: 'AES-GCM-256';
}

/**
 * Decryption result with metadata
 */
export interface DecryptionResult<T = string> {
  /** Decrypted data */
  data: T;
  /** Whether decryption was successful */
  success: boolean;
  /** Error message if decryption failed */
  error?: string;
  /** Key used for decryption */
  keyId?: string;
}

/**
 * Encryption options
 */
export interface EncryptionOptions {
  /** PHI classification for audit logging */
  classification?: PHIClassification;
  /** Additional authenticated data (AAD) for AES-GCM */
  additionalData?: string;
  /** User ID performing the encryption (for audit) */
  userId?: string;
}

/**
 * Audit log entry for PHI access
 * Required by HIPAA for minimum 6-year retention
 */
export interface PHIAuditLog {
  /** Unique log entry ID */
  id: string;
  /** Timestamp of access */
  timestamp: number;
  /** User who accessed the data */
  userId: string;
  /** Type of access */
  accessType: 'read' | 'write' | 'delete' | 'export';
  /** Patient ID whose data was accessed */
  patientId: string;
  /** PHI classification of accessed data */
  classification: PHIClassification;
  /** Specific fields accessed (for granular audit) */
  fieldsAccessed: string[];
  /** IP address of access */
  ipAddress?: string;
  /** User agent (browser/device) */
  userAgent?: string;
  /** Result of access attempt */
  result: 'success' | 'denied' | 'error';
  /** Error message if access failed */
  errorMessage?: string;
}

/**
 * Type helper to mark specific fields as encrypted in a type
 */
export type EncryptedFields<T, K extends keyof T> = Omit<T, K> & {
  [P in K]: EncryptedField;
};

/**
 * Type helper to mark all string fields as potentially encrypted
 */
export type EncryptedPHI<T> = {
  [P in keyof T]: T[P] extends string
    ? EncryptedField | T[P]
    : T[P] extends string | undefined
    ? EncryptedField | T[P]
    : T[P];
};
